import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute, Params} from '@angular/router';
import {LiteLoginService} from '../shared/lite-login.service';
import {LiteLoginAccess} from '../shared/lite-login-access.model';
import {NavigationService} from '../../shared/services/navigation.service';
import {Country} from '../../user-dashboard/shared/country.model';
import {CountryService} from '../../user-dashboard/shared/country.service';
import {Language, FullyTypeSafeLanguages, Languages} from '../../core/languages';
import {Carrier} from '../../user-dashboard/shared/carrier.model';
import {catchError, finalize, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {EMPTY} from 'rxjs';
import {AuthenticationService} from '../../shared/services/authentication.service';
import {LoaderService} from '../../shared/services/loader.service';
import {CarrierService} from '../../user-dashboard/shared/carrier.service';
import {ErrorHandlerService} from '../../shared/services/error-handler.service';
import {ModalService} from '../../shared/services/modal.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-lite-login-accesses-form',
  templateUrl: './lite-login-accesses-form.component.html',
  styleUrls: ['./lite-login-accesses-form.component.scss']
})
export class LiteLoginAccessesFormComponent implements OnInit {

  mode = 'edit';
  updating = false;
  activate = false;

  access: LiteLoginAccess = new LiteLoginAccess();

  carriers: Carrier[] = [];
  carrier: Carrier;

  countries: Country[] = [];

  languages: Language[] = [];

  deliveryNumber: string;
  loadId: string;
  warning = false;
  loadInfoError = false;
  loadErrorSource: 'delivery' | 'load';
  loading = false;
  loadingLoad = false;

  @ViewChild('reassignPopup', { static: true })
  reassignPopup: TemplateRef<any>;
  @ViewChild('errorPopup', { static: true })
  errorPopup: TemplateRef<any>;

  constructor(
    private liteLoginService: LiteLoginService,
    private carrierService: CarrierService,
    private countryService: CountryService,
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private loaderService: LoaderService,
    private errorHandlerService: ErrorHandlerService,
    private modalService: ModalService,
    private translate: TranslateService,
  ) {
    if (this.authService.canManageDrivers()) {
      this.loaderService.startLoad();
      this.carrierService.getSelfCarrier().pipe(
        tap(carrier => {
          this.carrier = carrier;
          localStorage.setItem('lite-login.carrierId', carrier.id);
        }),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'lite-login.errors.fetch-carrier');
          return EMPTY;
        }),
        finalize(() => this.loaderService.stopLoad())
      ).subscribe()
    } else {
      this.loaderService.startLoad();
      this.carrierService.getAllLight().pipe(
        tap( carriers => {
          this.carriers = carriers;
          const savedCarrierId = localStorage.getItem('lite-login.carrierId');
          if (savedCarrierId) {
            this.carrier = carriers.find(carrier => carrier.id === savedCarrierId);
          }
        }),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'lite-login.errors.fetch-carriers');
          return EMPTY;
        }),
        finalize(() => this.loaderService.stopLoad())
      ).subscribe()
    }

    this.route.params.subscribe((params: Params) => {
      const accessId = params.id;

      if (accessId) {
        this.updating = true;
        this.liteLoginService.findById(accessId)
          .subscribe(access => {
            this.access = access;
            this.loadId = access.tmsLoadsInfo[0].loadId;
          });
      }

      this.countryService.getAll()
        .subscribe(countries => this.countries = countries);

      this.languages = Languages.values();
    });

    this.route.data.subscribe(data => this.mode = data.mode);
  }

  ngOnInit(): void {
  }

  searchLoadInfoByDeliveryNumber() {
    this.loadingLoad = true;
    this.loaderService.startLoad();
    this.liteLoginService.findLoadInfoByDeliveryNumber(this.deliveryNumber, this.carrier.id)
      .pipe(finalize(() => {
        this.loadingLoad = false;
        this.loaderService.stopLoad();
      }))
      .subscribe(loadInfo => {
        this.loadId = loadInfo.loadId;
        this.access.tmsLoadsInfo = [loadInfo];
        this.loadInfoError = false;
      }, () => {
        this.loadInfoError = true;
        this.loadErrorSource = 'delivery';
      });
  }

  completeForm() {
    this.loading = true;
    this.loaderService.startLoad();
    this.liteLoginService.findLoadInfoByLoadId(this.loadId, this.carrier.id)
      .pipe(finalize(() => {
        this.loading = false;
        this.loaderService.stopLoad();
        })
      ).subscribe(loadInfo => {
        this.loadId = loadInfo.loadId;
        this.access.tmsLoadsInfo = [loadInfo];

        const warning = loadInfo.assignedVehicle !== `lite_${this.access.emailAddress.split('@')[0]}` &&
          loadInfo.assignedVehicle !== null;

        if (!warning) {
          this.createOrUpdate(this.activate);
        } else {
          this.modalService.open({
            title: this.translate.instant('lite-login.errors.load-info-reassign.title'),
            content: this.reassignPopup,
            data: {
              loadInfo: loadInfo
            },
            size: 'sm',
            validateButton: {
              text: this.translate.instant('lite-login.errors.load-info-reassign.validate')
            }
          }).subscribe(() => this.createOrUpdate(this.activate));
        }
      }, () => {
        this.loadInfoError = true;
        this.loadErrorSource = 'load';
      });
  }

  createOrUpdate(activate: boolean) {
    this.loaderService.startLoad();
    (this.updating ?
        this.liteLoginService.update(this.access, this.carrier.id, activate) :
        this.liteLoginService.create(this.access, this.carrier.id, activate)
    ).subscribe(() => {
      this.loaderService.stopLoad();
      this.returnToList();
    }, (error) => {
      this.modalService.open({
        title: this.translate.instant('lite-login.errors.title'),
        content: this.errorPopup,
        data: { error: this.translate.instant('lite-login.errors.activation') }
      }).subscribe();
      this.loaderService.stopLoad();
      this.returnToList();
    });
  }

  returnToList() {
    this.navigationService.liteLogin().list().go();
  }

  updateCarrier(carrier: Carrier) {
    this.carrier = carrier;
    this.access.carrierId = carrier.id;
    localStorage.setItem('lite-login.carrierId', carrier.id);
  }
}
